import { useQuery } from '@tanstack/react-query';
import { Checkbox, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mlm/ui';
import { listBranches, type Branch, type BranchTypeFilter } from '../lib/api';
import { useAuthStore, useIsSuperAdmin } from '../lib/auth-store';

// Radix's Select can't hold an empty string as a value, so "no filter" rides a
// sentinel that is mapped back to undefined at the component boundary.
const ALL = '__all';

/**
 * The branch list, shared by every picker on the page — one network call no
 * matter how many render.
 *
 * NOTE: `GET /branches` is unauthenticated today (no preHandler on the route).
 * Out of scope here; flagged so nobody assumes the picker is what protects it.
 */
export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
    staleTime: 5 * 60_000,
  });
}

/**
 * The branches this admin is confined to; empty = unconfined. Mirrors the
 * server's `effectiveBranchScopeIds`, including its fallback to the legacy
 * single POS pin, so the UI and the clamp can't disagree.
 */
export function useConfinedBranchIds(): string[] {
  const ids = useAuthStore((s) => s.user?.branchIds);
  const pin = useAuthStore((s) => s.user?.branchId ?? null);
  const isSuper = useIsSuperAdmin();
  if (isSuper) return [];
  if (ids && ids.length > 0) return ids;
  return pin ? [pin] : [];
}

/**
 * True when this admin is confined at all. The server clamps their results
 * regardless, so an unrestricted picker would only be a lie — see
 * `scopedFallback` for what renders instead when they hold exactly one branch.
 */
export function useIsBranchScoped(): boolean {
  return useConfinedBranchIds().length > 0;
}

const label = (b: Branch) => `${b.name} (${b.code})`;

/** The branch a scoped operator is pinned to, resolved to a display label. */
export function useScopedBranchLabel(): string | null {
  const confined = useConfinedBranchIds();
  const { data: branches } = useBranches();
  if (confined.length !== 1) return null;
  const b = (branches ?? []).find((x) => x.id === confined[0]);
  return b ? label(b) : null;
}

/** Single-select branch filter — the Reports filter row and the dashboard bar. */
export function BranchPicker({
  value,
  onChange,
  label: fieldLabel = 'Branch',
  allLabel = 'All branches',
  scopedFallback = 'hidden',
  filterType,
}: {
  value: string | undefined;
  onChange: (branchId: string | undefined) => void;
  label?: string;
  allLabel?: string;
  /**
   * Narrow the offered list to one `branches.type` ('branch' | 'stockist').
   * Paired with `BranchTypePicker` so the two controls can't contradict each
   * other — without it you could select a plain branch while filtering to
   * stockists and get an empty report with no visible cause. Client-side only:
   * `Branch.type` already rides the `GET /branches` payload.
   */
  filterType?: string;
  /**
   * What a branch-scoped operator sees. 'hidden' (default) renders nothing —
   * the long-standing Reports behaviour. 'label' renders a static, read-only
   * badge naming their branch: on the dashboard the numbers are already pinned
   * and several cards carry an "All branches" marker, so showing no cause at
   * all is more confusing than showing an uneditable one.
   */
  scopedFallback?: 'hidden' | 'label';
}) {
  const { data: branches } = useBranches();
  const confined = useConfinedBranchIds();
  const scopedLabel = useScopedBranchLabel();

  // Exactly one branch: there is nothing to pick, so keep the long-standing
  // read-only treatment.
  if (confined.length === 1) {
    if (scopedFallback === 'hidden') return null;
    return (
      <div>
        <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
        <div className="flex h-10 items-center rounded-md border border-dashed bg-muted/40 px-3 text-sm text-muted-foreground">
          {scopedLabel ?? 'Your branch'}
        </div>
      </div>
    );
  }

  // Several branches: a REAL picker, limited to theirs. The sentinel means
  // "all MY branches", not "all branches" — the label must not overstate what
  // they can see. Sending no branchId returns their whole permitted set, which
  // is exactly the auto-filtered default the feature asks for.
  const options = (branches ?? [])
    .filter((b) => !filterType || b.type === filterType)
    .filter((b) => confined.length === 0 || confined.includes(b.id));

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
        <SelectTrigger className="h-10 min-w-[190px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{confined.length > 0 ? 'All my branches' : allLabel}</SelectItem>
          {options.map((b) => (
            <SelectItem key={b.id} value={b.id}>
              {label(b)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Branch TYPE filter. A stockist is not a separate entity — it's a branch row
 * with `branches.type = 'stockist'` — so this narrows the same dimension as
 * `BranchPicker` and sits beside it. Today only Stock on Hand uses it.
 *
 * Hidden for a branch-scoped operator: their one branch already has exactly one
 * type, so the control could only ever blank out their own report.
 */
export function BranchTypePicker({
  value,
  onChange,
}: {
  value: BranchTypeFilter | undefined;
  onChange: (v: BranchTypeFilter | undefined) => void;
}) {
  const scoped = useIsBranchScoped();
  if (scoped) return null;
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">Type</p>
      <Select
        value={value ?? ALL}
        onValueChange={(v) => onChange(v === ALL ? undefined : (v as BranchTypeFilter))}
      >
        <SelectTrigger className="h-10 min-w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All types</SelectItem>
          <SelectItem value="branch">Branches only</SelectItem>
          <SelectItem value="stockist">Stockists only</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Multi-select branch filter — the Reports filter row.
 *
 * A near-identical component lived here once, for the dashboard's
 * Sales-per-Branch card, and was removed when the dashboard grew a single
 * page-level branch filter: two branch controls on one page contradict each
 * other. That objection is specific to the dashboard and does NOT apply to
 * Reports, which has exactly one filter row. The dashboard keeps its single
 * `BranchPicker`; do not wire this one in there.
 *
 * An EMPTY selection means "every branch you may see", not "none": that is both
 * this control's convention and the server's default when `?branchId=` is
 * absent, so the two agree without a translation layer. It also means a branch
 * added later is included by default rather than silently missing from every
 * saved filter.
 *
 * Unlike the version that was deleted, this one is scope-aware — the tick boxes
 * are the operator's OWN branches when they're confined to several.
 */
export function BranchCheckboxList({
  selected,
  onChange,
  label: fieldLabel = 'Branches',
  filterType,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  label?: string;
  /** Same contract as `BranchPicker.filterType` — keeps the two controls honest. */
  filterType?: string;
}) {
  const { data: branches } = useBranches();
  const confined = useConfinedBranchIds();

  // Exactly one branch: nothing to tick. Matches the Reports filter row's
  // long-standing `scopedFallback='hidden'` treatment for the single picker.
  if (confined.length === 1) return null;

  const options = (branches ?? [])
    .filter((b) => !filterType || b.type === filterType)
    .filter((b) => confined.length === 0 || confined.includes(b.id));
  if (options.length === 0) return null;

  const allOn = selected.length === 0;
  const toggle = (id: string) => {
    // The first click off "all" materializes the full set minus the one being
    // unchecked — otherwise unchecking one branch would blank the whole table.
    const base = allOn ? options.map((b) => b.id) : selected;
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    // Everything ticked is the same thing as no filter; store it as such.
    // Measured against `options`, NOT the raw branch list: an operator confined
    // to 3 of 10 branches could never reach the "all" state otherwise.
    onChange(next.length === options.length ? [] : next);
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{fieldLabel}</p>
      <div className="flex min-h-10 flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border px-3 py-2">
        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <Checkbox checked={allOn} onCheckedChange={() => onChange([])} />
          <span className={allOn ? 'font-medium' : 'text-muted-foreground'}>
            {confined.length > 0 ? 'All mine' : 'All'}
          </span>
        </label>
        {options.map((b) => {
          const on = allOn || selected.includes(b.id);
          return (
            <label
              key={b.id}
              title={label(b)}
              className="flex cursor-pointer items-center gap-1.5 text-xs"
            >
              <Checkbox checked={on} onCheckedChange={() => toggle(b.id)} />
              <span className={on ? '' : 'text-muted-foreground'}>{b.code}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
